import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * 卡片组件
 * 提供统一的卡片UI容器，支持多种变体和交互状态
 *
 * @example
 * ```tsx
 * // 基本用法
 * <Card>
 *   <h3>卡片标题</h3>
 *   <p>卡片内容</p>
 * </Card>
 *
 * // 使用不同变体
 * <Card variant="bordered">带边框的卡片</Card>
 * <Card variant="elevated">有阴影的卡片</Card>
 *
 * // 可点击的卡片
 * <Card clickable onClick={() => console.log('clicked')}>
 *   点击我
 * </Card>
 *
 * // 带页眉页脚的卡片
 * <Card
 *   header={<h3>标题</h3>}
 *   footer={<button>操作</button>}
 * >
 *   卡片内容
 * </Card>
 * ```
 */
import { forwardRef } from 'react';
import { cn } from '../../utils/cn';
/**
 * 获取卡片变体样式
 */
const getVariantStyles = (variant) => {
    const variants = {
        default: 'bg-white border',
        bordered: 'bg-white border-3',
        elevated: 'bg-white border shadow-md',
        flat: 'bg-gray-50',
    };
    return variants[variant];
};
/**
 * 获取卡片尺寸样式
 */
const getSizeStyles = (size) => {
    const sizes = {
        small: 'p-3',
        medium: 'p-4',
        large: 'p-6',
    };
    return sizes[size];
};
/**
 * 获取交互样式
 */
const getInteractiveStyles = (clickable, hoverable) => {
    let styles = '';
    if (clickable) {
        styles += ' cursor-pointer transition-all duration-200 hover:shadow-lg active:scale-[0.98]';
    }
    if (hoverable && !clickable) {
        styles += ' transition-all duration-200 hover:shadow-md';
    }
    return styles;
};
const BORDER_COLOR_MAP = {
    default: 'border-purple-500',
    'royal-blue': 'border-interactive-royal-blue',
    'sunset-orange': 'border-interactive-sunset-orange',
    'beach-orange': 'border-static-beach-orange-200',
    'emerald-flash': 'border-interactive-emerald-flash',
    'electric-yellow': 'border-interactive-electric-yellow',
    'coral-red': 'border-interactive-coral-red',
};
/**
 * 卡片组件
 *
 * 通用的卡片容器组件，提供统一的视觉风格和交互行为
 */
export const Card = forwardRef(({ variant = 'default', size = 'medium', clickable = false, hoverable = false, colorVariant = 'default', header, footer, className, children, onClick, ...props }, ref) => {
    const baseStyles = 'rounded-lg overflow-hidden';
    const variantStyles = getVariantStyles(variant);
    const sizeStyles = getSizeStyles(size);
    const interactiveStyles = getInteractiveStyles(clickable, hoverable);
    // 根据 variant 和 colorVariant 确定边框颜色
    let borderColorClass = '';
    if (variant === 'bordered') {
        borderColorClass = BORDER_COLOR_MAP[colorVariant] || BORDER_COLOR_MAP.default;
    }
    else if (variant === 'default' || variant === 'elevated') {
        borderColorClass = 'border-gray-200'; // 对于非 bordered 卡片，使用统一的灰色边框
    }
    const cardClasses = cn(baseStyles, variantStyles, borderColorClass, sizeStyles, interactiveStyles, className);
    const handleClick = (e) => {
        if (clickable && onClick) {
            onClick(e);
        }
    };
    const handleKeyDown = (e) => {
        if (clickable && onClick && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            // 对于键盘事件，我们使用更安全的类型转换
            onClick(e);
        }
    };
    return (_jsxs("div", { ref: ref, className: cardClasses, onClick: handleClick, role: clickable ? 'button' : undefined, tabIndex: clickable ? 0 : undefined, onKeyDown: clickable ? handleKeyDown : undefined, ...props, children: [header && (_jsx("div", { className: "border-b border-gray-200 pb-3 mb-3 last:border-b-0 last:pb-0 last:mb-0", children: header })), children && _jsx("div", { className: "flex-1", children: children }), footer && (_jsx("div", { className: "border-t border-gray-200 pt-3 mt-3 first:border-t-0 first:pt-0 first:mt-0", children: footer }))] }));
});
Card.displayName = 'Card';
export default Card;
//# sourceMappingURL=Card.js.map